import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { GtmService } from '@xbh/common-lib';
import { filter } from 'rxjs/operators';
import { NavMenuComponent } from './shared/nav-menu/nav-menu.component';
import { PageFooterComponent } from './shared/page-footer/page-footer.component';
import { PageHeaderComponent } from './shared/page-header/page-header.component';

/** PCブレークポイント（styles/_breakpoints.scss の $pc と同期） */
const PC_MEDIA_QUERY = '(min-width: 768px)';

/** ヘッダー操作（用語解説・FAQ・閉じる・メニュー）を非表示にするルート */
const ERROR_ROUTE_PATH = '/fsthp-app/error';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, PageHeaderComponent, PageFooterComponent, NavMenuComponent],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
})
export class App implements OnInit {
  protected readonly title = signal('fsthp');
  protected readonly isMenuOpen = signal(false);

  private readonly router = inject(Router);
  private readonly gtmService = inject(GtmService);
  private readonly destroyRef = inject(DestroyRef);
  private bodyOverflow = '';

  /** エラー画面ではヘッダー内の操作群を出さない */
  protected readonly showHeaderActions = signal(!this.isErrorUrl(this.router.url));

  ngOnInit() {
    const GTM_ID = 'GTM-K2WC2FV';
    // GTMコンテナIDを指定
    this.gtmService.init(GTM_ID);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.updateHeaderActionsVisibility(event.urlAfterRedirects);
        this.gtmService.pushEvent({
          event: 'page_view',
          url: event.urlAfterRedirects,
        });
      });

    const pcMediaQuery = window.matchMedia(PC_MEDIA_QUERY);
    const onPcMediaChange = (event: MediaQueryListEvent): void => {
      if (event.matches) {
        this.closeMenu();
      }
    };
    pcMediaQuery.addEventListener('change', onPcMediaChange);
    this.destroyRef.onDestroy(() => {
      pcMediaQuery.removeEventListener('change', onPcMediaChange);
      this.unlockBodyScroll();
    });
  }

  protected openMenu(): void {
    this.lockBodyScroll();
    this.isMenuOpen.set(true);
  }

  protected closeMenu(): void {
    this.unlockBodyScroll();
    this.isMenuOpen.set(false);
  }

  private updateHeaderActionsVisibility(url: string): void {
    const showActions = !this.isErrorUrl(url);
    this.showHeaderActions.set(showActions);
    if (!showActions) {
      this.closeMenu();
    }
  }

  private isErrorUrl(url: string): boolean {
    return url.split('?')[0] === ERROR_ROUTE_PATH;
  }

  private lockBodyScroll(): void {
    this.bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = this.bodyOverflow;
  }
}
